import {PatientTherapySequencesColumnsDef, useTherapyDayHeaders} from '@/modules/patients-therapies/components'
import {openPatientTherapySequenceDrawer} from '@/modules/patients-therapies/components/patient-therapy-sequence-drawer/util'
import {InfiniteDataTableV2, type InfiniteDataTableV2Props} from '@cypro/ui/components/ui/infinite-data-table'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {chakra} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'
import {useMemo} from 'react'
import {LuPill} from 'react-icons/lu'

// Activity labels some shared columns differently from Production planning
function columnHeading(key: string) {
  return function ColumnHeading() {
    const {t} = useTranslate('common')
    return <chakra.div width="100%">{t(key)}</chakra.div>
  }
}

// oxlint-disable-next-line typescript/no-empty-object-type
interface Props<TStartRow> extends Omit<
  InfiniteDataTableV2Props<PatientTherapySequence, TStartRow>,
  'emptyState' | 'columns'
> {
  dateFilterActive: boolean
}

export function PatientsTherapiesSequencesTable<TStartRow>({
  table: tableConfig,
  virtualizer: virtualizerConfig,
  dateFilterActive,
  ...rest
}: Props<TStartRow>) {
  const {t} = useTranslate('common')
  const {isHeaderRow, renderHeader, highlightRow} = useTherapyDayHeaders(!dateFilterActive)

  const columns = useMemo(
    () => [
      PatientTherapySequencesColumnsDef.observationsBubble({size: 36}),
      PatientTherapySequencesColumnsDef.time({size: 90}),
      // Activity is the only table that shows the hospital ID next to the patient name
      PatientTherapySequencesColumnsDef.patientWithHospitalId({size: 300}),
      PatientTherapySequencesColumnsDef.protocol({size: 170}),
      PatientTherapySequencesColumnsDef.treatment({size: 180, header: columnHeading('medication')}),
      PatientTherapySequencesColumnsDef.administration({size: 150}),
      PatientTherapySequencesColumnsDef.internalId({size: 150}),
      PatientTherapySequencesColumnsDef.journey({size: 150, header: columnHeading('status')}),
      PatientTherapySequencesColumnsDef.actions({size: 120}),
    ],
    []
  )

  return (
    <InfiniteDataTableV2<PatientTherapySequence, TStartRow>
      {...rest}
      table={{
        ...tableConfig,
        columns,
        defaultColumn: {minSize: 60, maxSize: 800},
      }}
      virtualizer={virtualizerConfig}
      scrollX
      resizableColumns
      columnSizingKey="activity-table-columns"
      isHeaderRow={isHeaderRow}
      renderHeader={renderHeader}
      highlightRow={highlightRow}
      emptyState={{
        title: t('no_sequences'),
        description: t('create_a_new_sequence_to_get_started'),
        icon: LuPill,
      }}
      rowHeight={64}
      onRowClick={openPatientTherapySequenceDrawer}
    />
  )
}
